import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { getTeacherLanding } from './content';
import ContactModal from './ContactModal';
import NotFoundPage from '../components/NotFoundPage';
import './teacherLanding.css';

export default function TeacherLandingShell() {
  const { teacherId } = useParams();
  const location = useLocation();
  const content = useMemo(() => getTeacherLanding(teacherId), [teacherId]);
  const [contactOpen, setContactOpen] = useState(false);

  const base = `/users/teacher/${teacherId}/landing`;

  const openContact = useCallback(() => setContactOpen(true), []);
  const closeContact = useCallback(() => setContactOpen(false), []);

  useEffect(() => {
    if (!contactOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeContact();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [contactOpen, closeContact]);

  if (!content) {
    return <NotFoundPage />;
  }

  const pathAfter = location.pathname.replace(/\/$/, '').slice(base.length);
  const active =
    pathAfter === '' || pathAfter === '/'
      ? 'home'
      : pathAfter.replace(/^\//, '').split('/')[0];

  return (
    <div className="tl-root">
      <header className="tl-header">
        <div className="tl-wrap tl-header-inner">
          <Link to={base} className="tl-logo">
            Key<span>2</span>English
          </Link>
          <nav className="tl-nav" aria-label="Landing">
            {content.nav.map((item) =>
              item.action === 'contact' ? (
                <button
                  key={item.id}
                  type="button"
                  className="tl-nav-link"
                  onClick={openContact}
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.id}
                  to={item.path ? `${base}/${item.path}` : base}
                  className={active === item.id || (item.id === 'home' && active === 'home') ? 'is-active' : undefined}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>
          <button type="button" className="tl-btn tl-btn-primary tl-btn-sm" onClick={openContact}>
            {content.ctaLabel}
          </button>
        </div>
      </header>

      <Outlet context={{ content, base, openContact }} />

      <footer className="tl-footer">
        <div className="tl-wrap tl-footer-grid">
          <div>
            <div className="tl-logo">
              Key<span>2</span>English
            </div>
            <p style={{ marginTop: '0.75rem', maxWidth: '22rem' }}>{content.footer.tagline}</p>
          </div>
          <div>
            <h4>{content.footer.navHeading}</h4>
            <ul>
              {content.nav.map((item) => (
                <li key={`f-${item.id}`}>
                  {item.action === 'contact' ? (
                    <button type="button" className="tl-nav-link" onClick={openContact}>
                      {item.label}
                    </button>
                  ) : (
                    <Link to={item.path ? `${base}/${item.path}` : base}>{item.label}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4>{content.footer.contactsHeading}</h4>
            <ul>
              <li>
                <a href={content.links.telegram} target="_blank" rel="noopener noreferrer">
                  Telegram
                </a>
              </li>
              <li>
                <a href={content.links.instagram} target="_blank" rel="noopener noreferrer">
                  {content.links.instagramHandle}
                </a>
              </li>
            </ul>
            {content.legal ? (
              <>
                <h4 style={{ marginTop: '1.35rem' }}>{content.footer.legalHeading}</h4>
                <ul>
                  <li>
                    <Link to={`${base}/${content.legal.path}`}>{content.legal.footerLabel}</Link>
                  </li>
                </ul>
              </>
            ) : null}
          </div>
        </div>
        <div className="tl-wrap tl-footer-bottom">{content.footer.copyright}</div>
      </footer>

      {contactOpen ? <ContactModal content={content} onClose={closeContact} /> : null}
    </div>
  );
}
